// src/services/customerService.ts
import { 
    collection, 
    query, 
    where, 
    getDocs, 
    doc, 
    getDoc,
    orderBy,
    limit as firestoreLimit,
    startAfter,
    type QueryConstraint
  } from 'firebase/firestore';
  import { db } from '../config/firebase';
  
  export interface Customer {
    id: string;
    name: string;
    email: string;
    phone: string;
    totalOrders: number;
    totalSpent: number;
    status: 'active' | 'inactive';
    lastOrder: Date;
    createdAt: Date;
    address: {
      street: string;
      city: string;
      state: string;
      country: string;
      postalCode: string;
    };
  }
  
  interface CustomerQueryOptions {
    status?: string;
    startAfterDoc?: any;
    limit?: number;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    search?: string;
  }
  
  class CustomerService {
    private customersCollection = collection(db, 'customers');
  
    async getCustomers(options: CustomerQueryOptions = {}): Promise<{ customers: Customer[]; lastDoc: any }> {
      try {
        const constraints: QueryConstraint[] = [];
  
        if (options.status && options.status !== 'all') {
          constraints.push(where('status', '==', options.status));
        }
  
        if (options.sortBy) {
          constraints.push(orderBy(options.sortBy, options.sortDirection || 'desc'));
        } else {
          constraints.push(orderBy('createdAt', 'desc'));
        }
  
        if (options.limit) {
          constraints.push(firestoreLimit(options.limit));
        }
  
        if (options.startAfterDoc) {
          constraints.push(startAfter(options.startAfterDoc));
        }
  
        const q = query(this.customersCollection, ...constraints);
        const snapshot = await getDocs(q);
  
        const customers = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Customer[];
  
        if (options.search) {
          const searchLower = options.search.toLowerCase();
          return {
            customers: customers.filter(customer => 
              customer.name.toLowerCase().includes(searchLower) ||
              customer.email.toLowerCase().includes(searchLower) ||
              customer.phone.includes(searchLower)
            ),
            lastDoc: snapshot.docs[snapshot.docs.length - 1]
          };
        }
  
        return {
          customers,
          lastDoc: snapshot.docs[snapshot.docs.length - 1]
        };
      } catch (error) {
        console.error('Error fetching customers:', error);
        throw error;
      }
    }
  
    async getCustomer(id: string): Promise<Customer | null> {
      try {
        const docRef = doc(this.customersCollection, id);
        const docSnap = await getDoc(docRef);
  
        if (!docSnap.exists()) return null;
  
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Customer;
      } catch (error) {
        console.error('Error fetching customer:', error);
        throw error;
      }
    }
  
    async getTopCustomers(limit: number = 5): Promise<Customer[]> {
      try {
        const q = query(
          this.customersCollection,
          orderBy('totalSpent', 'desc'),
          firestoreLimit(limit)
        );
  
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Customer[];
      } catch (error) {
        console.error('Error fetching top customers:', error);
        throw error;
      }
    }
  }
  
  export const customerService = new CustomerService();